import { Link } from '@tanstack/react-router'
import { CheckCircle2, ShieldCheck, UserCheck } from 'lucide-react'

import type { DualControlResult } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

/**
 * Saying, before the click, that an action is countersigned.
 *
 * The failure this prevents is small and constant: someone fills in a dialog, presses the button,
 * and only then learns that nothing happened and a second person has to agree. Nothing was lost,
 * but the operator now distrusts the screen — and worse, they may go and tell the group that the
 * leader has changed.
 *
 * What the notice can honestly say has two halves, and they are different kinds of statement.
 *
 * The **control** is a fact about the action, taken from the API contract: group creation, leader
 * change, officer assignment, and meeting-location changes are dual-controlled. That is always
 * true and is always stated.
 *
 * The **outcome** is not knowable here. The approval policy lives on the server, it is
 * branch-configurable, and it may demand a second person even from someone who holds both halves
 * of the permission pair. So the notice reports the one thing the browser does know — whether this
 * user's own role holds the countersigning code — and phrases the consequence as a likelihood
 * rather than a promise. The definite answer comes from `awaitingApproval` on the response, which
 * is what {@link DualControlOutcome} renders.
 */
export function DualControlNotice({
  action,
  approvePermission,
  canCountersign,
}: {
  /** The action, as a noun phrase: "Changing the leader". */
  action: string
  /** The countersigning permission code, named so an operator can ask for it by name. */
  approvePermission: string
  canCountersign: boolean
}) {
  return (
    <Alert tone={canCountersign ? 'info' : 'warning'}>
      {canCountersign ? <ShieldCheck aria-hidden /> : <UserCheck aria-hidden />}
      <div className="space-y-1">
        <AlertTitle>{action} is countersigned</AlertTitle>
        <AlertDescription>
          {canCountersign ? (
            <>
              Your role holds <span className="font-mono text-xs">{approvePermission}</span>, so this
              may complete as soon as you confirm it. If the branch&apos;s approval policy still
              calls for a second person, it is queued instead and this dialog says so.
            </>
          ) : (
            <>
              Your role does not hold <span className="font-mono text-xs">{approvePermission}</span>,
              so confirming this stores the request and changes nothing yet. Someone holding that
              permission has to countersign it before it takes effect — tell the group after that
              has happened, not now.
            </>
          )}
        </AlertDescription>
      </div>
    </Alert>
  )
}

/**
 * What actually happened.
 *
 * `awaitingApproval: true` means the command is stored and nothing has changed, so the wording is
 * "sent for approval" and never "saved". The request id is shown because it is what someone quotes
 * when they ring the approver.
 */
export function DualControlOutcome({
  result,
  awaitingDescription,
  appliedDescription,
}: {
  result: DualControlResult
  /** What is now waiting, in one line. */
  awaitingDescription: string
  /** What has now changed, in one line. */
  appliedDescription: string
}) {
  if (result.awaitingApproval) {
    return (
      <Alert tone="warning">
        <UserCheck aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Sent for approval — nothing has changed yet</AlertTitle>
          <AlertDescription>
            {awaitingDescription} It takes effect when a second person countersigns it, and until
            then the group reads exactly as it did before.
            {result.approvalRequestId ? (
              <>
                {' '}
                Request{' '}
                <span className="font-mono text-xs">{result.approvalRequestId}</span>.
              </>
            ) : null}
          </AlertDescription>
          <Button asChild variant="secondary" size="sm">
            <Link to="/approvals" search={{ tab: 'mine' }}>
              Track it under Submitted by me
            </Link>
          </Button>
        </div>
      </Alert>
    )
  }

  return (
    <Alert tone="success">
      <CheckCircle2 aria-hidden />
      <div className="space-y-1">
        <AlertTitle>Done</AlertTitle>
        <AlertDescription>{appliedDescription}</AlertDescription>
      </div>
    </Alert>
  )
}
